import { NonIdealState } from "@blueprintjs/core"
import React, { useState } from "react"
import { ReconciliationContainer } from "./Reconciliation.styled"
import ReconciliationGrid from "./components/ReconciliationGrid"
import SubmitConfirmation from "./components/SubmitConfirmation"
import SummaryBar from "./components/SummaryBar"
import UploadBar from "./components/UploadBar"
import { useReconciliation } from "./useReconciliation"

const Reconciliation: React.FC = () => {
  const {
    selectedFile,
    sourceType,
    setSourceType,
    result,
    statusFilter,
    setStatusFilter,
    handleFileChange,
    handleCompare,
    isLoading,
    edits,
    effectiveClasses,
    navControls,
    thresholds,
    priceGapBreaches,
    displayDiffs,
    displayTotals,
    correctionHistory,
    handleCellEdit,
    handleShareClassEdit,
    shareClassEdits,
    shareCountAcceptances,
    handleShareCountAcceptance,
    canSubmit,
    handleSubmit,
    isSubmitting,
  } = useReconciliation()

  // Submit goes through the confirmation (CONTEXT.md, "Acknowledgement"):
  // the fund-and-date question on a clean day, the list of NAV and
  // price-gap breaches to acknowledge otherwise.
  const [isConfirmOpen, setConfirmOpen] = useState(false)

  const onConfirmSubmit = (acknowledged: boolean) => {
    setConfirmOpen(false)
    if (!result?.account_id) return
    handleSubmit(result.account_id, acknowledged)
  }

  return (
    <ReconciliationContainer>
      <UploadBar
        selectedFile={selectedFile}
        onFileChange={handleFileChange}
        sourceType={sourceType}
        onSourceTypeChange={setSourceType}
        onCompare={handleCompare}
        isLoading={isLoading}
        // Open once a compare has run — closed only while a share-count
        // breach stands unaccepted (the hook's canSubmit).
        canSubmit={canSubmit}
        isSubmitting={isSubmitting}
        onSubmit={() => setConfirmOpen(true)}
      />

      {result && (
        <SubmitConfirmation
          isOpen={isConfirmOpen}
          fund={result.source_a_name}
          navDate={result.nav_date}
          navControls={navControls}
          priceGapBreaches={priceGapBreaches}
          onConfirm={onConfirmSubmit}
          onCancel={() => setConfirmOpen(false)}
        />
      )}

      {result && (
        <SummaryBar
          result={result}
          totals={displayTotals}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          shareClassEdits={shareClassEdits}
          onShareClassEdit={handleShareClassEdit}
          correctionHistory={correctionHistory}
          shareCountAcceptances={shareCountAcceptances}
          onShareCountAcceptance={handleShareCountAcceptance}
          effectiveClasses={effectiveClasses}
          navControls={navControls}
          thresholds={thresholds}
          priceGapBreaches={priceGapBreaches}
          displayDiffs={displayDiffs}
        />
      )}

      {result ? (
        <ReconciliationGrid
          result={result}
          displayDiffs={displayDiffs}
          statusFilter={statusFilter}
          edits={edits}
          correctionHistory={correctionHistory}
          onCellEdit={handleCellEdit}
        />
      ) : (
        <NonIdealState
          icon="comparison"
          title="No reconciliation data"
          description="Upload a file and click Compare to reconcile positions."
        />
      )}
    </ReconciliationContainer>
  )
}

export default Reconciliation
